import { action, computed, observable, store } from '~/common/mobx.decorator'
import flatten from 'lodash/flatten'
import { persist } from 'mobx-persist'
import { projectFilterValues } from '~/common/helpers/table-filter-values.helper'

// A range filter's value is `{ min, max }`. String(object) is "[object Object]"
// for every range, so Apply treated a new range as the one already stored.
export const filterValueKey = (value) => {
	if (value && typeof value === 'object') {
		try {
			return JSON.stringify(value)
		} catch (error) {
			return String(value)
		}
	}
	return String(value)
}

// `mergeFromKeys` folds in what generic-table-v2's own singleton used to keep:
// the two stores held DIFFERENT tables (v2 owned Home › Matches, Home ›
// Submissions, Candidates › Matches and Candidates › Per Diem), so the union is
// exactly the user's filters, with nothing to reconcile.
@store({ persist: true, persistKey: 'GenericTableStore', mergeFromKeys: ['GenericTableStoreV2'] })
class GenericTableStore {
	@observable sortField
	@observable sortOrder

	@observable innerSortField
	@observable innerSortOrder

	@persist('object') @observable appliedFilters = {} // Structure: { [tableName]: { [filterKey]: [filterItems] } }
	@observable popOverHeight = 0

	@persist('object') @observable recentFilters = {}

	@computed
	get hasAppliedFilters() {
		return Object.keys(this.appliedFilters).length > 0
	}

	@computed
	get allFilters() {
		return flatten(Object.values(this.appliedFilters))
	}

	@computed
	get allRecentFilters() {
		return flatten(Object.values(this.recentFilters))
	}

	@computed
	get allFiltersValue() {
		// Get filter items for query param
		return Object.keys(this.appliedFilters)?.reduce((accumulative, currentKey) => {
			accumulative[currentKey] = this.appliedFilters[currentKey]?.map((item) => item.value)

			return accumulative
		}, {})
	}

	// Get filters for a specific table
	getFiltersForTable = (tableName) => {
		if (!tableName) return {}
		return this.appliedFilters[tableName] || {}
	}

	// Get filter values for a specific table. The projection — including which
	// keys are scalars rather than lists — is shared with the inner tables.
	getFiltersValueForTable = (tableName) => {
		if (!tableName) return {}
		return projectFilterValues(this.getFiltersForTable(tableName))
	}

	// Check if a table has applied filters
	hasAppliedFiltersForTable = (tableName) => {
		if (!tableName) return false
		const tableFilters = this.getFiltersForTable(tableName)
		return Object.keys(tableFilters).length > 0
	}

	@action
	addRecentFilter = (filterKey, filterItem) => {
		// A range is an object. String(object) is "[object Object]" for every range,
		// so a second Apply looked like the same filter and was dropped.
		const normalizedFilterValue = filterValueKey(filterItem.value)
		const existedFilterItem = this.recentFilters[filterKey]?.find((item) => filterValueKey(item.value) === normalizedFilterValue)
		if (existedFilterItem && filterValueKey(existedFilterItem.value) === normalizedFilterValue) {
			return
		}

		if (this.recentFilters[filterKey]?.length >= 5) {
			const [firstElements] = this.recentFilters[filterKey]
			const normalizedFirstValue = filterValueKey(firstElements.value)

			Object.keys(this.recentFilters).forEach((recentFilterKey) => {
				const matchedFilterItem = this.recentFilters[recentFilterKey]?.find((item) => filterValueKey(item.value) === normalizedFirstValue)

				if (matchedFilterItem) {
					this.recentFilters[recentFilterKey] = this.recentFilters[recentFilterKey].filter((filter) => filterValueKey(filter.value) !== normalizedFirstValue)
				}
			})
		}

		this.recentFilters[filterKey] = [...(this.recentFilters[filterKey] ?? []), filterItem]
	}

	@action
	addFilter = (filterKey, filterItem, tableName) => {
		if (!tableName) {
			// Fallback to old behavior for backward compatibility
			this.appliedFilters[filterKey] = [...(this.appliedFilters[filterKey] ?? []), filterItem]
			this.addRecentFilter(filterKey, filterItem)
			return
		}

		const table = this.appliedFilters[tableName] ?? {}

		// Dedupe: never store the same value twice for a key (you can't pick the
		// same option twice). Prevents duplicate filter pills.
		const existing = table[filterKey] ?? []
		if (existing.some((it) => filterValueKey(it?.value) === filterValueKey(filterItem?.value))) {
			return
		}

		/*
		 * Reassign the map rather than writing into the nested object: this is MobX 5,
		 * which has no Proxy for plain observable objects, so a key that was not there
		 * when the object became observable is invisible to the `filterFields`
		 * computeds that read it.
		 */
		this.appliedFilters = {
			...this.appliedFilters,
			[tableName]: { ...table, [filterKey]: [...existing, filterItem] },
		}
		this.addRecentFilter(filterKey, filterItem)
	}

	@action
	removeFilter = (filterKey, filterValue, tableName) => {
		const normalizedFilterValue = filterValueKey(filterValue)

		if (!tableName) {
			// Fallback to old behavior for backward compatibility
			if (!this.appliedFilters[filterKey]) {
				return
			}
			this.appliedFilters[filterKey] = this.appliedFilters[filterKey].filter((filter) => filterValueKey(filter.value) !== normalizedFilterValue)
			if (this.appliedFilters[filterKey]?.length > 0) {
				return
			}
			delete this.appliedFilters[filterKey]
			return
		}

		if (!this.appliedFilters[tableName] || !this.appliedFilters[tableName][filterKey]) {
			return
		}

		this.appliedFilters[tableName][filterKey] = this.appliedFilters[tableName][filterKey].filter(
			(filter) => filterValueKey(filter.value) !== normalizedFilterValue
		)

		if (this.appliedFilters[tableName][filterKey]?.length > 0) {
			return
		}

		delete this.appliedFilters[tableName][filterKey]

		// Clean up empty table entry
		if (Object.keys(this.appliedFilters[tableName]).length === 0) {
			delete this.appliedFilters[tableName]
		}
	}

	@action
	clearAllFilters = (tableName) => {
		if (!tableName) {
			// Fallback to old behavior for backward compatibility
			this.appliedFilters = {}
			return
		}

		if (this.appliedFilters[tableName]) {
			delete this.appliedFilters[tableName]
		}
	}

	/**
	 * Clear filters for an explicit set of tables.
	 *
	 * Used by page-level resets (the X in the universal search box), which used
	 * to call `clearAllFilters()` with no table name and therefore wiped the
	 * filters of every table in the app — including tables on other pages the
	 * user had not touched.
	 */
	@action
	clearFiltersForTables = (tableNames) => {
		if (!Array.isArray(tableNames)) return
		tableNames.forEach((tableName) => {
			if (tableName && this.appliedFilters[tableName]) {
				delete this.appliedFilters[tableName]
			}
		})
	}

	@action
	clearAllSortFilter = () => {
		this.appliedFilters = {}
		this.sortField = undefined
		this.sortOrder = undefined
	}

	@action
	onUpdateSortField = (sortField) => {
		if (sortField !== this.sortField) {
			this.sortField = sortField
		}
	}

	@action
	onUpdateSortOrder = (sortOrder) => {
		if (sortOrder !== this.sortOrder) {
			this.sortOrder = sortOrder
		}
	}

	@action
	onUpdateSort = (sortField, sortOrder) => {
		this.onUpdateSortField(sortField)
		this.onUpdateSortOrder(sortOrder)
	}

	@action
	onUpdateInnerSortField = (innerSortField) => {
		if (innerSortField !== this.innerSortField) {
			this.innerSortField = innerSortField
		}
	}

	@action
	onUpdateInnerSortOrder = (innerSortOrder) => {
		if (innerSortOrder !== this.innerSortOrder) {
			this.innerSortOrder = innerSortOrder
		}
	}

	@action
	onUpdateInnerSort = (innerSortField, innerSortOrder) => {
		this.onUpdateInnerSortField(innerSortField)
		this.onUpdateInnerSortOrder(innerSortOrder)
	}

	@action
	setDefaultSort = (sortField, sortOrder) => {
		if (!this.sortField && !this.sortOrder) {
			this.sortField = sortField
			this.sortOrder = sortOrder
		}
	}

	@action
	setDefaultInnerSort = (innerSortField, innerSortOrder) => {
		if (!this.innerSortField && !this.innerSortOrder) {
			this.innerSortField = innerSortField
			this.innerSortOrder = innerSortOrder
		}
	}

	@action
	updatePopoverHeight = (height) => {
		this.popOverHeight = height
	}
}

export const genericTableStore = new GenericTableStore()
